import type { AllFeatures, WorkbookUiFeatureName } from 'cadenza/features';
import { getDependentFeatures, isSingleFeatureAvailable, isWorkbookFeature } from 'cadenza/features';
import type { WorkbookState } from 'cadenza/workbook/workbook.state';
import { slice } from 'cadenza/workbook/workbook.state';
import type { RootStateOr } from 'cadenza/store/cadenza-store';
import { isOperationModeSimplified } from 'cadenza/workbook/operation-mode';
import { getSetting, isSettingSet } from 'cadenza/workbook/workbook-settings-util';
import { keys } from 'cadenza/utils/type-util';

const SIMPLIFIED_MODE_FEATURE_DEFAULTS: Partial<Record<WorkbookUiFeatureName, boolean>> = {
  'workbook-analysis-context': true,
  'workbook-analysis-context-closeable': true,
  'workbook-data-view-analysis-context': false,
  'workbook-data-view-content': false,
  'workbook-show-filtered-views': false,
  'workbook-design': true,
  'workbook-drillthrough-other-workbooks': true,
  'workbook-drillthrough-same-workbook': true,
  'workbook-drillthrough-external-links': true,
  'workbook-print-worksheet': true,
  'workbook-worksheet-pdf-export': true,
  'workbook-layout-management': false,
  'workbook-map-add-layer-from-catalog': true,
  'workbook-map-add-layer-from-geodata-service': true,
  'workbook-map-add-layer-from-navigator': true,
  'workbook-map-add-data-view-layer': true,
  'workbook-map-add-grid-square-layer': true,
  'workbook-map-add-group-layer': true,
  'workbook-map-add-layer-from-data-import': true,
  'workbook-map-add-sketch-layer': true,
  'workbook-map-add-data-acquisition-layer': true,
  'workbook-map-add-analytics-extension-layer': true,
  'workbook-map-data-acquisition': false,
  'workbook-map-find-address': true,
  'workbook-map-find-coordinate': true,
  'workbook-map-find-what3words': true,
  'workbook-map-isochrones': true,
  'workbook-map-location-finder': true,
  'workbook-map-measure': true,
  'workbook-map-routing': true,
  'workbook-map-timeline-3d': true,
  'workbook-map-timeline-player': true,
  'workbook-map-toolbar': true,
  'workbook-table-cell-details': true,
  'workbook-table-single-measure-values': true,
  'workbook-view-pdf-export': true,
  'workbook-view-data-and-images-export': true,
  'workbook-worksheet-menu': true,
};

const SIMPLIFIED_MODE_FEATURES = new Set<WorkbookUiFeatureName>([
  ...keys(SIMPLIFIED_MODE_FEATURE_DEFAULTS),

  'workbook-condition-management',
  'workbook-data-browser',
  'workbook-drillthrough-external-links',
  'workbook-drillthrough-postmessage',
  'workbook-view-management',
  'workbook-management',
]);

export function isWorkbookFeatureAvailable(state: RootStateOr<WorkbookState>, feature: AllFeatures): boolean {
  return (
    isSingleWorkbookFeatureAvailable(state, feature) &&
    getDependentFeatures(feature).every((dependentFeature) => isWorkbookFeatureAvailable(state, dependentFeature))
  );
}

function isSingleWorkbookFeatureAvailable(state: RootStateOr<WorkbookState>, feature: AllFeatures) {
  return (
    isSingleFeatureAvailable(feature) &&
    !(
      isWorkbookFeature(feature) &&
      SIMPLIFIED_MODE_FEATURES.has(feature) &&
      isDisabledBecauseOfSimplifiedMode(state, feature)
    )
  );
}

export function isDisabledBecauseOfSimplifiedMode(state: RootStateOr<WorkbookState>, feature: WorkbookUiFeatureName) {
  return isOperationModeSimplified(state) && !isEnabledInSimplifiedMode(state, feature);
}

export function isEnabledInSimplifiedMode(state: RootStateOr<WorkbookState>, feature: WorkbookUiFeatureName) {
  const setting = getSimplifiedModeSetting(feature);
  return setting != null && getSetting(slice(state), setting, SIMPLIFIED_MODE_FEATURE_DEFAULTS[feature]);
}

export function isSimplifiedModeSettingChanged(state: RootStateOr<WorkbookState>, feature: WorkbookUiFeatureName) {
  const setting = getSimplifiedModeSetting(feature);
  return setting != null && isSettingSet(slice(state), setting);
}

export function getSimplifiedModeSetting(feature: WorkbookUiFeatureName): string | undefined {
  if (feature in SIMPLIFIED_MODE_FEATURE_DEFAULTS) {
    return `workbook.simplified.${feature}`;
  }
}
